import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Paper,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const Contact = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Crear el cuerpo del mensaje con formato
    const emailBody = `
Nombre: ${formData.name}
Email: ${formData.email}

Mensaje:
${formData.message}
    `.trim();

    // Crear la URL mailto con los datos del formulario
    const mailtoLink = `mailto:fj.aduery@gmail.com?subject=Mensaje de contacto de ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(emailBody)}`;
    
    // Abrir el cliente de correo
    window.location.href = mailtoLink;

    // Mostrar mensaje de éxito
    setSnackbar({
      open: true,
      message: t('contact.success'),
      severity: 'success'
    });
    
    // Limpiar el formulario
    setFormData({ name: '', email: '', message: '' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <LocationOnIcon sx={{ fontSize: 32 }} />,
      title: t('contact.info.location'),
      value: 'Santiago, Chile'
    },
    {
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      title: t('contact.info.email'),
      value: 'fj.aduery@gmail.com',
      link: 'mailto:fj.aduery@gmail.com'
    },
   {
   icon: <PhoneIcon sx={{ fontSize: 32 }} />,
      title: t('contact.info.phone'),
      value: '(+56) 9 4260 5456'
    },
    {
      icon: <LinkedInIcon sx={{ fontSize: 32 }} />,
      title: t('contact.info.linkedin'),
      value: 'fco-aguirred',
      link: 'https://www.linkedin.com/in/fco-aguirred/'
    }
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: 8,
        background: 'linear-gradient(180deg, #0a1428 0%, #050810 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 2,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #ffffff 30%, #22e165 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('contact.title')}
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                mb: 6,
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 500
              }}
            >
              {t('contact.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(34, 225, 101, 0.05)',
                    border: '1px solid rgba(34, 225, 101, 0.1)',
                    borderRadius: 4
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      color: 'white',
                      fontWeight: 600
                    }}
                  >
                    {t('contact.title')}
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label={t('contact.name')}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(34, 225, 101, 0.5)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label={t('contact.email')}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(34, 225, 101, 0.5)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label={t('contact.message')}
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(34, 225, 101, 0.5)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        background: 'linear-gradient(45deg, #22e165 30%, #1a8f3d 90%)',
                        color: 'white',
                        py: 1.5,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1a8f3d 30%, #22e165 90%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(34, 225, 101, 0.3)'
                        }
                      }}
                    >
                      {t('contact.send')}
                    </Button>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(34, 225, 101, 0.05)',
                    border: '1px solid rgba(34, 225, 101, 0.1)',
                    borderRadius: 4
                  }}
                >
                  <Box>
                    {contactInfo.map((info, index) => (
                      <Box
                        key={index}
                        component={info.link ? 'a' : 'div'}
                        href={info.link}
                        target={info.link ? '_blank' : undefined}
                        rel={info.link ? 'noopener noreferrer' : undefined}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          background: 'rgba(34, 225, 101, 0.05)',
                          border: '1px solid rgba(34, 225, 101, 0.1)',
                          transition: 'all 0.3s ease',
                          cursor: info.link ? 'pointer' : 'default',
                          textDecoration: 'none',
                          '&:hover': info.link ? {
                            background: 'rgba(34, 225, 101, 0.1)',
                            transform: 'translateY(-2px)',
                            borderColor: 'rgba(34, 225, 101, 0.3)'
                          } : {},
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.primary.main,
                            mr: 2
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              mb: 0.5
                            }}
                          >
                            {info.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: 'white',
                              fontWeight: 500
                            }}
                          >
                            {info.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            background: 'rgba(34, 225, 101, 0.1)',
            border: '1px solid rgba(34, 225, 101, 0.2)',
            color: 'white',
            '& .MuiAlert-icon': {
              color: theme.palette.primary.main
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;